import React, { useState, useEffect } from 'react';
import { Lightbulb, AlertTriangle, Check, Trash2, Clock, User, Calendar, Tag } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

const SuggestionsWidget = () => {
  const { suggestions = [], updateSuggestion, deleteSuggestion } = useData();
  const [activeTab, setActiveTab] = useState('improvement');
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'handled'

  // כאן ממשיך כל שאר הקוד...
  // סינון הצעות לפי סוג
  const filteredSuggestions = suggestions.filter(suggestion => {
    const matchesType = suggestion.type === activeTab;
    const matchesStatus = filter === 'all' || 
      (filter === 'pending' && suggestion.status === 'pending') ||
      (filter === 'handled' && suggestion.status === 'handled');
    return matchesType && matchesStatus;
  });

  // ספירת הודעות לא מטופלות
  const pendingImprovements = suggestions.filter(s => s.type === 'improvement' && s.status === 'pending').length;
  const pendingIssues = suggestions.filter(s => s.type === 'issue' && s.status === 'pending').length;

  const handleMarkAsHandled = async (suggestionId) => {
    try {
      await updateSuggestion(suggestionId, { 
        status: 'handled',
        handledAt: new Date()
      });
    } catch (error) {
      console.error('Error marking suggestion as handled:', error);
    }
  };

  const handleDelete = async (suggestionId) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק הודעה זו?')) {
      try {
        await deleteSuggestion(suggestionId);
      } catch (error) {
        console.error('Error deleting suggestion:', error);
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical': return 'קריטית';
      case 'high': return 'גבוהה';
      case 'medium': return 'בינונית';
      case 'low': return 'נמוכה';
      default: return priority;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('he-IL', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">הצעות ייעול ודיווחי תקלות</h2>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">הכל</option>
            <option value="pending">ממתין לטיפול</option>
            <option value="handled">טופל</option>
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-lg mb-6">
        <button
          onClick={() => setActiveTab('improvement')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'improvement' 
              ? 'bg-white text-blue-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <Lightbulb size={18} />
            <span>הצעות ייעול</span>
            {pendingImprovements > 0 && (
              <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingImprovements}
              </span>
            )}
          </div>
        </button>
        <button
          onClick={() => setActiveTab('issue')}
          className={`flex-1 py-2 px-4 rounded-md font-medium transition-all ${
            activeTab === 'issue' 
              ? 'bg-white text-red-600 shadow-sm' 
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <div className="flex items-center justify-center gap-2">
            <AlertTriangle size={18} />
            <span>דיווחי תקלות</span>
            {pendingIssues > 0 && (
              <span className="bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingIssues}
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {filteredSuggestions.length === 0 ? (
          <p className="text-center text-gray-500 py-8">
            אין {activeTab === 'improvement' ? 'הצעות ייעול' : 'דיווחי תקלות'} 
            {filter === 'pending' ? ' הממתינים לטיפול' : filter === 'handled' ? ' שטופלו' : ''}
          </p>
        ) : (
          filteredSuggestions.map((suggestion) => (
            <div 
              key={suggestion.id} 
              className={`border rounded-lg p-4 transition-all ${
                suggestion.status === 'handled' 
                  ? 'bg-gray-50 border-gray-300' 
                  : activeTab === 'improvement'
                    ? 'border-blue-200 bg-blue-50'
                    : 'border-red-200 bg-red-50'
              }`}
            >
              {/* Header Row */}
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900">{suggestion.title}</h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <User size={14} />
                      <span>{suggestion.userName}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar size={14} />
                      <span>{formatDate(suggestion.createdAt)}</span>
                    </div>
                    {suggestion.department && (
                      <div className="flex items-center gap-1">
                        <Tag size={14} />
                        <span>{suggestion.department}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                    {getPriorityLabel(suggestion.priority)}
                  </span>
                  {suggestion.status === 'handled' && (
                    <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium flex items-center gap-1">
                      <Check size={12} />
                      טופל
                    </span>
                  )}
                </div>
              </div>

              {/* Description */}
              <p className="text-gray-700 mb-3 whitespace-pre-wrap">{suggestion.description}</p>

              {/* Actions */}
              <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  {suggestion.status === 'handled' && suggestion.handledAt && (
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      טופל ב-{formatDate(suggestion.handledAt)}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {suggestion.status === 'pending' && (
                    <button
                      onClick={() => handleMarkAsHandled(suggestion.id)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center gap-1"
                    >
                      <Check size={16} />
                      סמן כטופל
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(suggestion.id)}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center gap-1"
                  >
                    <Trash2 size={16} />
                    מחק
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary */}
      {suggestions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm text-gray-600">
            <span>
              סה"כ: {suggestions.length} הודעות
            </span>
            <span>
              ממתין לטיפול: {suggestions.filter(s => s.status === 'pending').length}
            </span>
            <span>
              טופל: {suggestions.filter(s => s.status === 'handled').length}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuggestionsWidget;